/**
 * Schémas Zod — Intake Events.
 *
 * Source DB : `app.intake_events` (migration 0021).
 *
 * Lecture seule jusqu'à 9.F. Chunk 9.G.1 ajoute les transitions resolve/reject
 * (transitions terminales métier — pas de POST/DELETE encore). Cf. ADR 0023.
 */

import { z } from "zod";

import { GUARANTEES } from "../constants.js";

import { DateOnlySchema, UuidSchema } from "./common.js";
import { CreateCompanySchema } from "./companies.js";
// 9.V.2c-2 (F) — code assureur validé SYNTAXIQUEMENT (slug), découplé du
// catalogue statique. `target_insurers` devient des recipients résolus au
// materialize (assureur actif du tenant, cf. 9.V.2c-1) ; `apply_project_data.
// insurer_codes` alimente le hint projet (non-contraignant). Jamais de DB en Zod.
import { InsurerCodeSchema } from "./insurers.js";
import { CreateProjectSchema } from "./projects.js";

const GUARANTEE_CODES_TUPLE = GUARANTEES.map((g) => g.code) as [string, ...string[]];

// -----------------------------------------------------------------------------
// Enums (miroir DB)
// -----------------------------------------------------------------------------

export const IntakeSourceKindSchema = z.enum([
  "form_submission",
  "api_call",
  "ai_operator",
  "human_ui",
  "file_upload",
]);
export type IntakeSourceKind = z.infer<typeof IntakeSourceKindSchema>;

export const IntakeEventStatusSchema = z.enum([
  "pending_classification",
  "pending_resolution",
  "resolved",
  "rejected",
  "superseded",
]);
export type IntakeEventStatus = z.infer<typeof IntakeEventStatusSchema>;

/**
 * Chunk 9.T.5.v2.1 — Direction commerciale du document reçu via intake_events.
 * Distincte de `source_kind` (canal technique : form/api/ai/human/file_upload).
 * Enum 4 valeurs verrouillées PO 2026-07-13 Q2 :
 *   - `insurer_response`   — document reçu d'un assureur (cotation, offre)
 *   - `client_input`       — document/demande reçu(e) d'un client
 *   - `advisor_document`   — document reçu d'un conseiller/advisor externe
 *   - `unknown`            — direction non déterminée (backfill legacy OU choix
 *                            broker « Attacher au projet seulement »)
 *
 * Peuplée par les actions humaines post-materialize (9.T.5.v2.2 API +
 * 9.T.5.v2.3 UI). DEFAULT 'unknown' côté DB (backward compat). Labels FR/EN
 * via i18n `intake.doc_direction.*` (Q10).
 *
 * Cf. cadrage `docs/design/intake-doc-direction-cadrage.md` §5.
 */
export const DocDirectionSchema = z.enum([
  "insurer_response",
  "client_input",
  "advisor_document",
  "unknown",
]);
export type DocDirection = z.infer<typeof DocDirectionSchema>;

// -----------------------------------------------------------------------------
// Row (lecture)
// -----------------------------------------------------------------------------

export const IntakeEventRowSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,

  source_kind: IntakeSourceKindSchema,
  status: IntakeEventStatusSchema,

  /**
   * Chunk 9.T.5.v2.1 — direction commerciale du document reçu. NOT NULL côté
   * DB (DEFAULT 'unknown'). Peuplée par 9.T.5.v2.2 API + 9.T.5.v2.3 UI post-
   * materialize humain (parité PO Q1+Q3).
   */
  doc_direction: DocDirectionSchema,

  // Source actor polymorphe (au plus un non-null) — ADR 0021/0022.
  source_user_id: UuidSchema.nullable(),
  source_agent_id: UuidSchema.nullable(),
  source_api_key_id: UuidSchema.nullable(),
  source_external_app_id: UuidSchema.nullable(),

  payload: z.record(z.unknown()),
  payload_template_id: UuidSchema.nullable(),
  language_detected: z.string().nullable(),

  // Résolution
  resolved_company_id: UuidSchema.nullable(),
  resolved_project_id: UuidSchema.nullable(),
  resolved_quote_request_id: UuidSchema.nullable(),
  dedup_decision: z.record(z.unknown()).nullable(),
  rejection_reason: z.string().nullable(),
  superseded_by_id: UuidSchema.nullable(),

  resolved_at: z.string().nullable(),
  resolved_by_user: UuidSchema.nullable(),
  resolved_by_agent: UuidSchema.nullable(),

  /**
   * Lien d'intake F2 d'origine (chunk 9.N.3.B.0 — migration 0030).
   * NULL = event non issu d'un lien d'intake F2 (api_call, ai_operator,
   * human_ui, file_upload, ou form_submission legacy d'avant 0030).
   * Cascade SET NULL si le lien parent est supprimé pour préserver l'audit.
   */
  intake_link_id: UuidSchema.nullable(),

  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});
export type IntakeEventRow = z.infer<typeof IntakeEventRowSchema>;

// -----------------------------------------------------------------------------
// Broker-sanitized payload (Chunk D-NEW-C — anti-leak Loi 25 wire-side)
// -----------------------------------------------------------------------------
//
// Le broker authentifié reçoit cette **shape projetée** depuis :
//   - GET /v1/intake-events
//   - GET /v1/intake-events/:id
//   - MCP tools list_intake_events / get_intake_event (wrap les routes API)
//
// Le payload jsonb stocké en DB reste COMPLET (audit/compliance Loi 25 + worker
// retention need les champs full). Sanitization wire-only.
//
// Champs **EXCLUS** (vs `IntakeEventRowSchema.payload` qui peut contenir tout) :
//   - `payload.consent.ip_hash`         — bcrypt non-réversible mais sensible (anti-fingerprinting)
//   - `payload.files[].sha256`          — checksum interne, broker n'en a pas besoin
//   - `payload.files[].storage_bucket`  — identifiant Supabase Storage interne
//   - `payload.files[].storage_path`    — chemin Supabase interne (broker DL via /v1/files/:id/download)
//
// Champs **PRÉSERVÉS** : tous les autres (consent.{accepted,timestamp}, comment,
// email, files[].{name,mime,size}, source_meta, payload_template_id, etc.).

const IntakeEventBrokerSanitizedFileSchema = z
  .object({
    name: z.string().optional(),
    mime: z.string().optional(),
    size: z.number().optional(),
  })
  .passthrough(); // tolère champs futurs ; les sensibles sont strippés par le helper backend

const IntakeEventBrokerSanitizedConsentSchema = z
  .object({
    accepted: z.boolean().optional(),
    timestamp: z.string().optional(),
    // ip_hash absent par construction — strippé côté backend.
  })
  .passthrough();

export const IntakeEventBrokerSanitizedPayloadSchema = z
  .object({
    consent: IntakeEventBrokerSanitizedConsentSchema.optional(),
    comment: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    files: z.array(IntakeEventBrokerSanitizedFileSchema).optional(),
    source_meta: z
      .object({
        channel: z.string().optional(),
        ua_present: z.boolean().optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough();
export type IntakeEventBrokerSanitizedPayload = z.infer<
  typeof IntakeEventBrokerSanitizedPayloadSchema
>;

/**
 * Row projetée pour broker — identique à `IntakeEventRow` sauf le champ
 * `payload` qui suit la shape sanitized ci-dessus. Les autres champs (id,
 * tenant_id, source_*, resolved_*, dedup_decision, etc.) restent inchangés
 * — la sanitization se concentre sur le payload jsonb (la zone la plus
 * exposée à des fuites accidentelles).
 *
 * Note : `dedup_decision` jsonb non sanitized par D-NEW-C (hors scope —
 * actuellement vide en pre-prod). À auditer si exposition future émerge.
 */
export const IntakeEventBrokerSanitizedRowSchema = IntakeEventRowSchema.extend({
  payload: IntakeEventBrokerSanitizedPayloadSchema,
});
export type IntakeEventBrokerSanitizedRow = z.infer<typeof IntakeEventBrokerSanitizedRowSchema>;

// -----------------------------------------------------------------------------
// Filtres listing — GET /v1/intake-events
// -----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
// Resolve / Reject (Chunk 9.G.1)
// -----------------------------------------------------------------------------
//
// Décisions PO 9.G.1 :
//   Q1=A — `resolution_kind` limité à `project` | `quote_request` (pas de
//          `submission` Phase 1).
//   Q2=A — `target_id` doit exister et appartenir au même tenant. Pas de
//          création implicite (le broker doit avoir créé le projet/QR au
//          préalable).
//   Q3=A — `rejection_reason` free-text 1..500 chars. Pas d'enum vocabulaire
//          contrôlé Phase 1 (à reconsidérer si analytics futur émerge).
//   Q4=A — supersede hors scope (dette P3 9.G.4 documentée).
//   Q5=A — concurrency par check status atomic (WHERE status IN pending_*).
//          Pas de If-Match/ETag/updated_at Phase 1.
//
// `dedup_decision` jsonb peuplé automatiquement côté handler (pas exposé au
// broker) : `{kind:'human', actor_user_id, decided_at, resolution_kind, target_id}`.
// `resolved_at` + `resolved_by_user` peuplés handler-side.
// `resolved_company_id` reste NULL Phase 1 (cascade denorm = enhancement P3).

/**
 * Body PATCH `/v1/intake-events/:id/resolve` — broker-facing.
 *
 * Le handler valide en plus que `target_id` existe et appartient au tenant
 * du broker (404 `INTAKE_EVENTS_RESOLVE_TARGET_NOT_FOUND` sinon, anti-leak
 * unique pour les 2 cas not_found vs cross-tenant).
 */
export const ResolveIntakeEventSchema = z.object({
  resolution_kind: z.enum(["project", "quote_request"]),
  target_id: z.string().uuid(),
});
export type ResolveIntakeEventInput = z.infer<typeof ResolveIntakeEventSchema>;

/**
 * Body PATCH `/v1/intake-events/:id/reject` — broker-facing.
 *
 * `rejection_reason` requis 1..500 chars (free-text). Persisté dans la
 * colonne `app.intake_events.rejection_reason` (text nullable côté DB,
 * non-null après transition `rejected`). Visible côté lecture broker.
 */
export const RejectIntakeEventSchema = z.object({
  rejection_reason: z.string().min(1).max(500),
});
export type RejectIntakeEventInput = z.infer<typeof RejectIntakeEventSchema>;

// -----------------------------------------------------------------------------
// Filtres listing — GET /v1/intake-events
// -----------------------------------------------------------------------------

export const IntakeEventFilterSchema = z.object({
  project_id: UuidSchema.optional(),
  company_id: UuidSchema.optional(),
  source_kind: IntakeSourceKindSchema.optional(),
  status: IntakeEventStatusSchema.optional(),
  /** ISO datetime — borne basse `created_at >= from` (incluse). */
  from: z.string().datetime().optional(),
  /** ISO datetime — borne haute `created_at <= to` (incluse). */
  to: z.string().datetime().optional(),
  /**
   * Filtre 9.N.3.B.0 — listing broker des soumissions d'un lien d'intake F2
   * précis (page détail `/intake-links/:id`). UUID strict ; aucun filtre
   * spécial sur NULL pour ce paramètre (les events sans lien sont exclus
   * dès qu'on filtre par UUID).
   */
  intake_link_id: UuidSchema.optional(),
  /**
   * Recherche full-text simple (Chunk 9.N.3.C). Le handler applique un
   * `ILIKE` sur `payload->>'comment'` et `payload->>'email'` (champs stables
   * du payload jsonb 9.N.1.B2). Min 2 chars pour éviter scan trop large ;
   * max 100 chars pour éviter abuse. Pas d'index trigram dédié (la table
   * intake_events reste petite par tenant — recherche acceptable séquentielle
   * + filtres status/date/intake_link_id qui réduisent déjà drastiquement).
   */
  q: z.string().min(2).max(100).optional(),
});
export type IntakeEventFilter = z.infer<typeof IntakeEventFilterSchema>;

// -----------------------------------------------------------------------------
// 9.T.3 — Broker intake_event upload from PDF
//
// `POST /v1/intake-events` multipart upload broker-authenticated. Crée un
// intake_event orphan (intake_link_id=NULL) en `pending_classification` +
// rows files (intake_event_id parent migration 0044) + document_links
// polymorphes. Réponse minimale : juste l'id du nouvel event pour permettre
// au frontend de naviguer vers `/intake-events/:id` detail page.
// -----------------------------------------------------------------------------

export const BrokerIntakeUploadResponseSchema = z.object({
  status: z.literal("received"),
  intake_event_id: z.string().uuid(),
  file_ids: z.array(z.string().uuid()),
});
export type BrokerIntakeUploadResponse = z.infer<typeof BrokerIntakeUploadResponseSchema>;

// -----------------------------------------------------------------------------
// 9.T.4 — AI classifier + materialize route + lookup-driven suggestions
// -----------------------------------------------------------------------------
//
// Cadrage : docs/design/intake-event-materialize-contract.md
//
// Deux surfaces distinctes (PO Q5) :
//   - `POST /v1/intake-events/:id/classify`   — trigger classifier IA inline
//     (broker-auth uniquement Phase 1 via `assertHumanGated intake_event.classify`),
//     Promise.race 30s + fallback gracieux (soft-fail = 200 wire, pas d'erreur).
//   - `POST /v1/intake-events/:id/materialize`— crée company/project selon
//     3 stratégies (broker-auth via `assertHumanGated intake_event.materialize`).
//
// -----------------------------------------------------------------------------
// Payload jsonb — shape `intake_event.payload.classification`
// -----------------------------------------------------------------------------
//
// La colonne DB `app.intake_events.payload` (jsonb libre depuis migration 0021)
// contient `payload.classification` après un appel `/classify` réussi ou
// soft-fail. Le sanitizer broker strippe systématiquement les champs internes
// (classifier_version / raw_llm_response / input_tokens / output_tokens /
// cost_usd / duration_ms / model) — cf. `sanitizeIntakeEventRowForBroker`
// dans `apps/api/src/routes/intake-events.ts`. Décision PO Q8.
//
// Le schema ci-dessous décrit la shape WIRE (broker-facing) — donc SANS les
// champs internes strippés. La DB peut contenir des champs additionnels
// (`.passthrough()`) pour audit forensic.

/**
 * Candidate company détectée par le classifier. Tous les champs sont
 * `null`-able car le classifier peut ne pas trouver certains éléments.
 * Le broker confirme via UI (materialize) — la classification est advisory,
 * jamais prescriptive.
 */
export const ClassificationCandidateCompanySchema = z.object({
  legal_name: z.string().nullable(),
  postal_code: z.string().nullable(),
  business_number: z.string().nullable(),
  identification_type: z.string().nullable(),
});
export type ClassificationCandidateCompany = z.infer<typeof ClassificationCandidateCompanySchema>;

/**
 * Candidate contact détecté par le classifier. Parité candidate_company —
 * tous champs nullable, broker confirme.
 */
export const ClassificationCandidateContactSchema = z.object({
  name: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
});
export type ClassificationCandidateContact = z.infer<typeof ClassificationCandidateContactSchema>;

/**
 * Evidence snippet — 1..N courtes citations du PDF associées à un champ
 * candidat. Aide broker à vérifier la source de la suggestion sans télécharger
 * le PDF. Longueur volontairement limitée (anti-leak + coût output token).
 */
export const ClassificationEvidenceSchema = z.object({
  /** Nom du champ auquel se rattache l'evidence (ex. `candidate_company.legal_name`). */
  field: z.string(),
  /** Court extrait du PDF (`min 1, max 240 chars`). */
  snippet: z.string().min(1).max(240),
});
export type ClassificationEvidence = z.infer<typeof ClassificationEvidenceSchema>;

/**
 * Shape wire broker-facing du `payload.classification`. Deux branches :
 *   - `ok: true`  → classifier a réussi. Contient candidate_company/contact/kind/lang/confidence/evidence.
 *   - `ok: false` → soft-fail (timeout|parse_error|unavailable). Contient uniquement `reason`.
 *
 * Le sanitizer broker garantit l'absence des champs internes (model,
 * classifier_version, tokens, cost, duration_ms, raw_llm_response). Ceux-ci
 * restent en DB pour audit forensic mais ne sortent jamais du wire.
 *
 * `.passthrough()` sur les 2 branches pour tolérer des champs futurs additifs
 * sans breaking test (parité pattern IntakeEventBrokerSanitizedPayloadSchema).
 */
export const ClassificationPayloadSuccessSchema = z
  .object({
    ok: z.literal(true),
    kind: z.string().min(1).max(80),
    lang: z.string().min(2).max(8),
    confidence: z.number().min(0).max(1),
    candidate_company: ClassificationCandidateCompanySchema.nullable(),
    candidate_contact: ClassificationCandidateContactSchema.nullable(),
    evidence: z.array(ClassificationEvidenceSchema).max(20).default([]),
    attempted_at: z.string(),
  })
  .passthrough();

export const ClassificationPayloadSoftFailSchema = z
  .object({
    ok: z.literal(false),
    /**
     * 9.W.2 — `provider_configuration` ajouté : refus DÉFINITIF du fournisseur
     * d'IA (solde de crédit, clé, permission). Il ne doit PAS être confondu avec
     * `unavailable` (transitoire, retry utile) : ici retenter ne changera rien
     * tant qu'un administrateur n'est pas intervenu.
     */
    reason: z.enum(["timeout", "parse_error", "unavailable", "provider_configuration"]),
    attempted_at: z.string(),
  })
  .passthrough();

export const ClassificationPayloadSchema = z.discriminatedUnion("ok", [
  ClassificationPayloadSuccessSchema,
  ClassificationPayloadSoftFailSchema,
]);
export type ClassificationPayload = z.infer<typeof ClassificationPayloadSchema>;
export type ClassificationPayloadSuccess = z.infer<typeof ClassificationPayloadSuccessSchema>;
export type ClassificationPayloadSoftFail = z.infer<typeof ClassificationPayloadSoftFailSchema>;

// -----------------------------------------------------------------------------
// Wire response schema — POST /:id/classify
// -----------------------------------------------------------------------------
//
// Le handler retourne toujours 200 (sauf hard errors 404/409/403) avec :
//   - `intake_event` : la row sanitized D-NEW-C (mise à jour post-classify).
//   - `classification_status` : "success" | "soft_fail" — permet au frontend
//     de différencier sans avoir à parser `payload.classification.ok`.
//   - `soft_fail_reason` : présent uniquement si `classification_status ===
//     "soft_fail"`. Enum public i18n microcopy.

export const ClassifyIntakeEventResponseSchema = z.object({
  intake_event: IntakeEventBrokerSanitizedRowSchema,
  classification_status: z.enum(["success", "soft_fail"]),
  // 9.W.2 — `provider_configuration` : le frontend doit pouvoir dire « inutile
  // de réessayer, contactez un administrateur » plutôt que « réessayez ».
  soft_fail_reason: z
    .enum(["timeout", "parse_error", "unavailable", "provider_configuration"])
    .optional(),
});
export type ClassifyIntakeEventResponse = z.infer<typeof ClassifyIntakeEventResponseSchema>;

// -----------------------------------------------------------------------------
// Materialize body schema — POST /:id/materialize
// -----------------------------------------------------------------------------
//
// PO Q2 : 3 stratégies explicites (pas de discriminated union sur Zod pour
// garder validation permissive côté Zod + validations conditionnelles côté
// handler avec codes erreur granulaires — cf. `INTAKE_EVENTS_MATERIALIZE_
// STRATEGY_INVALID` + `_CONFIRM_REQUIRED`).
//
// Note : on définit le schema `new_company` et `new_project` en imports
// paresseux via `z.lazy(...)` pour éviter un import cyclique companies.ts ↔
// intake-events.ts. En pratique, on prend `CreateCompanySchema` +
// `CreateProjectSchema.omit({company_id})` (cadrage §2.3). `company_id` du
// new_project est dérivé serveur-side (existing_company_id ou id du nouveau
// company créé) — pas de chance de mismatch.

export const MaterializeStrategySchema = z.enum([
  "attach_existing_project",
  "attach_company_create_project",
  "create_company_and_project",
]);
export type MaterializeStrategy = z.infer<typeof MaterializeStrategySchema>;

/**
 * `CreateProjectSchema` sans `company_id` — dérivé serveur-side dans materialize.
 *
 * 9.T.5.v3.6 QA #3 — override `insurer_codes` pour rendre le champ facultatif
 * au materialize (défaut []). Le doc peut être un ajout au dossier, une pièce
 * justificative, un input client — sans assureur cible immédiat. Les assureurs
 * deviennent obligatoires SEULEMENT si `review_snapshot.create_quote_request`
 * est fourni à l'étape 5 du wizard (via `CreateQuoteRequestFromReviewSchema.
 * target_insurers.min(1)`). Le default `[]` reste valide DB (app.projects.
 * insurer_codes text[] DEFAULT '{}').
 */
export const MaterializeNewProjectSchema = CreateProjectSchema.omit({ company_id: true }).extend({
  insurer_codes: z.array(InsurerCodeSchema).max(10).default([]),
});
export type MaterializeNewProjectInput = z.infer<typeof MaterializeNewProjectSchema>;

/**
 * 9.T.5.v3.5 — `review_snapshot` optionnel — matérialisation enrichie.
 *
 * Alignements QA PO v3.5 §2 : `.strict()` sur chaque sous-schema pour bloquer
 * les champs supplémentaires silencieux. Registries assureurs/couvertures
 * enforced Zod-side (rejet 400 VALIDATION_ERROR pré-handler).
 * `target_insurers` unique via `.refine()` (Q4/QA §2). Dates via
 * `DateOnlySchema` ISO YYYY-MM-DD.
 *
 * Alignement QA §3 : `create_quote_request` optionnel — sa présence est la
 * SEULE façon de déclencher la création QR. Ne dépend PAS de la présence
 * `apply_project_data.insurer_codes`.
 *
 * Alignement QA §5 : `doc_direction` humaine explicite obligatoire quand
 * `review_snapshot` fourni.
 */
const ApplyProjectDataSchema = z
  .object({
    insurer_codes: z.array(InsurerCodeSchema).min(1).max(10).optional(),
    guarantee_codes: z.array(z.enum(GUARANTEE_CODES_TUPLE)).max(30).optional(),
    effect_date: DateOnlySchema.optional(),
    renewal_target_date: DateOnlySchema.optional(),
    deadline_date: DateOnlySchema.optional(),
  })
  .strict();

const ApplyCompanyDataSchema = z
  .object({
    employee_count: z.number().int().min(0).max(1_000_000).optional(),
  })
  .strict();

const CreateQuoteRequestFromReviewSchema = z
  .object({
    target_insurers: z
      .array(InsurerCodeSchema)
      .min(1)
      .max(10)
      .refine((arr) => new Set(arr).size === arr.length, {
        message: "target_insurers duplicate insurer_code — chaque code doit être unique",
      }),
    title: z.string().min(1).max(200).optional(),
    expected_response_by: z.string().datetime().optional(),
  })
  .strict();

export const ReviewSnapshotSchema = z
  .object({
    doc_direction: DocDirectionSchema,
    apply_project_data: ApplyProjectDataSchema.optional(),
    apply_company_data: ApplyCompanyDataSchema.optional(),
    create_quote_request: CreateQuoteRequestFromReviewSchema.optional(),
  })
  .strict();
export type ReviewSnapshotInput = z.infer<typeof ReviewSnapshotSchema>;

export const MaterializeIntakeEventSchema = z
  .object({
    strategy: MaterializeStrategySchema,

    /** Requis si `strategy === "attach_existing_project"`. */
    existing_project_id: z.string().uuid().optional(),

    /** Requis si `strategy === "attach_company_create_project"`. */
    existing_company_id: z.string().uuid().optional(),

    /**
     * Requis si `strategy === "create_company_and_project"`. Réutilise
     * `CreateCompanySchema` (parité POST /v1/companies). Validation Zod
     * complète (legal_name + language + cross-field business_number ×
     * identification_type appliquée handler-side via `validateBusinessNumberForType`).
     */
    new_company: CreateCompanySchema.optional(),

    /**
     * Requis si `strategy !== "attach_existing_project"`. Réutilise
     * `CreateProjectSchema.omit({company_id})` (le `company_id` est dérivé
     * serveur-side depuis `existing_company_id` ou l'id de la company créée).
     */
    new_project: MaterializeNewProjectSchema.optional(),

    /**
     * Anti-double-clic + trace audit explicite. PO Q3 : obligatoire pour
     * stratégies (b)+(c) uniquement. Le handler throw `_CONFIRM_REQUIRED`
     * 422 si absent/false pour ces 2 stratégies. Non requis pour (a).
     */
    confirmed_create: z.boolean().default(false),

    /**
     * 9.T.5.v3.5 — matérialisation enrichie (optionnel, backward compat 100%).
     * Absent → path 9.T.4 strict inchangé. Présent → application canoniques
     * Q7.a + création QR facultative + doc_direction humaine explicite.
     */
    review_snapshot: ReviewSnapshotSchema.optional(),
  })
  .strict();
export type MaterializeIntakeEventInput = z.infer<typeof MaterializeIntakeEventSchema>;

// -----------------------------------------------------------------------------
// Wire response schema — POST /:id/materialize
// -----------------------------------------------------------------------------
//
// Le handler retourne :
//   - `intake_event` : la row sanitized D-NEW-C (status=resolved, resolved_*
//                      peuplés, dedup_decision.strategy peuplé).
//   - `created_company` : présent uniquement si `strategy === "create_company_and_project"`.
//   - `created_project` : présent uniquement si `strategy !== "attach_existing_project"`.
//
// Anti-leak : pas de tenant_id / UUID user brut / payload raw event.
// Les rows created_* sont sanitized parité GET /:id de leur route respective.

export const MaterializeIntakeEventResponseSchema = z.object({
  intake_event: IntakeEventBrokerSanitizedRowSchema,
  /** CompanyRow si strategy === "create_company_and_project" sinon null. */
  created_company: z.record(z.unknown()).nullable(),
  /** ProjectRow si strategy !== "attach_existing_project" sinon null. */
  created_project: z.record(z.unknown()).nullable(),
  /**
   * 9.T.5.v3.5 — présent si `review_snapshot.create_quote_request` fourni.
   * QuoteRequestRow + recipients nested (parité POST /v1/quote-requests).
   * Absent (null) si materialize simple sans review_snapshot ou sans
   * create_quote_request explicite.
   */
  created_quote_request: z.record(z.unknown()).nullable().optional(),
  /**
   * 9.T.5.v3.5 — nombre de valeurs canoniques appliquées aux colonnes
   * company/project depuis `review_snapshot.apply_project_data` +
   * `apply_company_data`. Ne compte PAS les rows extracted_fields (QA §1).
   * Absent (undefined) si materialize simple sans review_snapshot.
   */
  applied_extracted_fields_count: z.number().int().min(0).optional(),
});
export type MaterializeIntakeEventResponse = z.infer<typeof MaterializeIntakeEventResponseSchema>;

// -----------------------------------------------------------------------------
// 9.T.5.v2.2 — Actions humain-only post-materialize (doc_direction switch)
// -----------------------------------------------------------------------------
//
// Trois endpoints déclenchent chacun une direction commerciale distincte
// (PO Q3 v2.0) sur un intake_event déjà `status='resolved'` avec
// `doc_direction='unknown'` :
//
//   POST /:id/create-submission-from-file    → doc_direction='insurer_response'
//   POST /:id/create-quote-request-from-file → doc_direction='advisor_document'
//   POST /:id/attach-project-only            → doc_direction='unknown'
//                                              (payload.direction_confirmed_at trace)
//
// Guards communs handler-side :
//   - `status='resolved'`                    (INTAKE_EVENT_ACTION_INVALID_STATUS)
//   - `doc_direction='unknown'`              (INTAKE_EVENT_ACTION_ALREADY_DIRECTED)
//   - `resolved_project_id NOT NULL`         (INTAKE_EVENT_ACTION_MISSING_PROJECT)
//
// Anti-scope : les 3 endpoints NE créent PAS de recipients quote_request
// (dette D-NEW future si besoin ; recipients toujours addable via
// POST /v1/quote-requests/:id/recipients).

/**
 * Body `POST /:id/create-submission-from-file` — 9.T.5.v2.2.
 *
 * `insurer_code` obligatoire (choix broker Phase 1 depuis classifier hints).
 * `insurer_name` optionnel — si absent, handler fallback `insurer_code` pour
 * garantir une string non-vide côté DB (colonne NOT NULL). Le broker peut
 * PATCH ensuite via `POST /v1/submissions/:id` s'il veut un nom lisible
 * distinct du code.
 * `detection_confidence` optionnel (0..1) — capturé pour audit forensic si le
 * broker a accepté un hint classifier vs saisi manuellement.
 */
export const CreateSubmissionFromFileSchema = z
  .object({
    insurer_code: z.string().min(1).max(80),
    insurer_name: z.string().min(1).max(200).optional(),
    detection_confidence: z.number().min(0).max(1).optional(),
    /**
     * Le créneau assureur auquel ce document répond — chunk 9.AP.1.
     *
     * Facultatif, et il ne le reste que tant qu'il n'y a **pas** d'ambiguïté :
     * quand plusieurs créneaux du dossier attendent ce même assureur, la route
     * refuse (`INTAKE_EVENT_ACTION_AMBIGUOUS_RECIPIENT`) et c'est par ce champ
     * que le conseiller répond « celui-là ». Le nommer ne dispense d'aucune
     * vérification : dossier, assureur et disponibilité sont contrôlés comme à
     * la création directe.
     */
    responds_to_recipient_id: z.string().uuid().optional(),
  })
  .strict();
export type CreateSubmissionFromFileInput = z.infer<typeof CreateSubmissionFromFileSchema>;

/**
 * Body `POST /:id/create-quote-request-from-file` — 9.T.5.v2.2.
 *
 * PO Q9 v2.2 : body minimal — pas de recipients[]. Les recipients seront
 * ajoutés via `POST /v1/quote-requests/:id/recipients` existant.
 * `title` optionnel — si absent, la QR est créée sans titre (broker peut
 * PATCH ensuite via POST /v1/quote-requests/:id... — hors scope v2.2).
 * `expected_response_by` optionnel timestamp ISO.
 */
export const CreateQuoteRequestFromFileSchema = z
  .object({
    title: z.string().min(1).max(200).optional(),
    expected_response_by: z.string().datetime().optional(),
  })
  .strict();
export type CreateQuoteRequestFromFileInput = z.infer<typeof CreateQuoteRequestFromFileSchema>;

/**
 * Body `POST /:id/attach-project-only` — 9.T.5.v2.2.
 *
 * Body volontairement vide (`.strict()` verrou anti-body-injection). L'action
 * confirme explicitement l'intention « attacher seulement — pas de submission
 * ni QR » et laisse `doc_direction='unknown'`. Trace explicite dans
 * `payload.direction_confirmed_at` (option iii D.1 v2.2 lock PO 2026-07-13).
 */
export const AttachProjectOnlyBodySchema = z.object({}).strict();
export type AttachProjectOnlyBodyInput = z.infer<typeof AttachProjectOnlyBodySchema>;

// -----------------------------------------------------------------------------
// Wire response schemas — 3 endpoints 9.T.5.v2.2
// -----------------------------------------------------------------------------
//
// Toutes les responses incluent `intake_event` (sanitized broker) + éventuel
// created entity. Pas de leak sensitive (parité materialize).

export const CreateSubmissionFromFileResponseSchema = z.object({
  intake_event: IntakeEventBrokerSanitizedRowSchema,
  /** SubmissionRow (sanitized via SUBMISSION_COLS handler-side). */
  created_submission: z.record(z.unknown()),
});
export type CreateSubmissionFromFileResponse = z.infer<
  typeof CreateSubmissionFromFileResponseSchema
>;

export const CreateQuoteRequestFromFileResponseSchema = z.object({
  intake_event: IntakeEventBrokerSanitizedRowSchema,
  /** QuoteRequestRow (sanitized via QUOTE_REQUEST_COLS handler-side). */
  created_quote_request: z.record(z.unknown()),
});
export type CreateQuoteRequestFromFileResponse = z.infer<
  typeof CreateQuoteRequestFromFileResponseSchema
>;

export const AttachProjectOnlyResponseSchema = z.object({
  intake_event: IntakeEventBrokerSanitizedRowSchema,
});
export type AttachProjectOnlyResponse = z.infer<typeof AttachProjectOnlyResponseSchema>;
